import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Button, Card, Dialog, EmptyState, ErrorState, StatusBadge, Tabs, Textarea, TextSkeleton, type Tone,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { formatDateTime } from '../../../lib/format';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';

/**
 * Questions from physicians about released values.
 *
 * A physician who doubts a value asks the laboratory that signed it rather
 * than changing it. The answer is "the value stands" with what was checked,
 * or — after correcting it through the laboratory's own amendment on
 * Validate & release — "amended". Until it is answered the fitness decision
 * waits. The bench sees the test, the value and the question beside the
 * subject label, never the worker's name or the decision.
 */

interface Row {
  id: string; subject: string; testName: string; valueSnapshot: string | null; flagSnapshot: string | null;
  question: string; status: 'OPEN' | 'CONFIRMED' | 'AMENDED' | 'CANCELLED';
  requestedAt: string; respondedAt: string | null; responseNote: string | null;
}

const TONE: Record<Row['status'], Tone> = { OPEN: 'pending', CONFIRMED: 'success', AMENDED: 'info', CANCELLED: 'neutral' };
const LABEL: Record<Row['status'], string> = {
  OPEN: 'Waiting for you', CONFIRMED: 'Confirmed', AMENDED: 'Amended', CANCELLED: 'Withdrawn by the physician',
};

export function Verifications() {
  const [tab, setTab] = useState<'OPEN' | 'ALL'>('OPEN');
  const canAnswer = holds(useAuth().permissions, 'lab.result-validate');
  const list = useQuery<{ items: Row[] }>(() => api.get(`/lab/verification-requests?status=${tab}`), [tab]);
  const [answering, setAnswering] = useState<Row | null>(null);

  return (
    <div>
      <PageHeader
        title="Verification requests"
        hint="Physicians asking the laboratory to check a released value. Confirm it, or amend it on Validate & release and then answer “amended”."
      />
      <Tabs
        active={tab}
        onChange={(t) => setTab(t as 'OPEN' | 'ALL')}
        tabs={[{ key: 'OPEN', label: 'Open' }, { key: 'ALL', label: 'All' }]}
      />
      <div className="mt-4">
        {list.loading && !list.data ? <TextSkeleton lines={5} />
          : list.error ? <ErrorState title="Could not load the requests" detail={list.error} onRetry={list.reload} />
          : (list.data?.items.length ?? 0) === 0 ? (
            <EmptyState title={tab === 'OPEN' ? 'No open questions' : 'No questions yet'} hint="When a physician questions a value you released, it appears here." />
          ) : (
            <ul className="space-y-3">
              {list.data!.items.map((r) => (
                <li key={r.id}>
                  <Card className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-small font-semibold text-neutral-900">{r.testName}{r.valueSnapshot ? ` · ${r.valueSnapshot}` : ''}
                          {r.flagSnapshot ? <span className="font-normal text-neutral-500"> ({r.flagSnapshot.replace(/_/g, ' ').toLowerCase()})</span> : null}
                        </p>
                        <p className="text-caption font-normal text-neutral-500">{r.subject} · asked {formatDateTime(r.requestedAt)}</p>
                      </div>
                      <StatusBadge tone={TONE[r.status]}>{LABEL[r.status]}</StatusBadge>
                    </div>
                    <p className="mt-2 text-small text-neutral-800">“{r.question}”</p>
                    {r.responseNote && (
                      <p className="mt-1 text-caption text-neutral-700">Your answer: “{r.responseNote}”{r.respondedAt ? ` · ${formatDateTime(r.respondedAt)}` : ''}</p>
                    )}
                    {r.status === 'OPEN' && canAnswer && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => setAnswering(r)}>Answer</Button>
                        <Link to="/lab/release" className="inline-flex items-center text-caption font-semibold text-primary-600 hover:underline">
                          Amend the value on Validate &amp; release
                        </Link>
                      </div>
                    )}
                  </Card>
                </li>
              ))}
            </ul>
          )}
      </div>
      {answering && <AnswerDialog row={answering} onClose={() => setAnswering(null)} onDone={() => { setAnswering(null); list.reload(); }} />}
    </div>
  );
}

function AnswerDialog({ row, onClose, onDone }: { row: Row; onClose: () => void; onDone: () => void }) {
  const [outcome, setOutcome] = useState<'CONFIRMED' | 'AMENDED'>('CONFIRMED');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post(`/lab/verification-requests/${row.id}/respond`, { outcome, note: note.trim() });
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={`Answer: ${row.testName}`} onClose={() => { if (!busy) onClose(); }}>
      <fieldset className="mt-2 space-y-1.5">
        <legend className="text-caption text-neutral-700">Outcome</legend>
        <label className="flex items-start gap-2 text-small">
          <input type="radio" name="outcome" checked={outcome === 'CONFIRMED'} onChange={() => setOutcome('CONFIRMED')} className="mt-0.5 h-4 w-4 accent-primary-600" />
          The value stands — it was checked and is correct
        </label>
        <label className="flex items-start gap-2 text-small">
          <input type="radio" name="outcome" checked={outcome === 'AMENDED'} onChange={() => setOutcome('AMENDED')} className="mt-0.5 h-4 w-4 accent-primary-600" />
          Amended — the corrected value has been validated and released
        </label>
      </fieldset>
      {outcome === 'AMENDED' && (
        <Alert tone="info" className="mt-3">Answer “amended” only after the corrected result is released. The original stays on the record beside it.</Alert>
      )}
      <label className="mt-3 block">
        <span className="text-caption text-neutral-700">What was checked</span>
        <Textarea rows={3} className="mt-1" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Re-run on the same sample: 21.4 g/dL. Analyser QC within limits." />
      </label>
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy} disabled={note.trim().length < 5}>Send answer</Button>
      </div>
    </Dialog>
  );
}
